import type { z } from 'zod'
import type {
  Erc20TokenTransfer,
  LowerHex,
  TransactionDecoded,
} from '../../../../types'
import { getAllValue } from './allValue'
import { checkValueIsArrayIn } from './arrayIn'
import { checkValueIsBetweenBigInt } from './betweenBigInt'
import type { erc20TransferPatternSchema } from './schema'

export const checkTransferIsMatchErc20Pattern = ({
  transfer,
  schema,
  transaction,
  relatedAddresses,
}: {
  readonly transfer: Erc20TokenTransfer
  readonly schema: z.infer<typeof erc20TransferPatternSchema>
  readonly transaction: TransactionDecoded
  readonly relatedAddresses: Set<LowerHex>
}): boolean => {
  // address
  if (
    schema.address !== undefined &&
    !checkValueIsArrayIn({
      value: transfer.address,
      schema: schema.address,
      transaction,
      relatedAddresses,
    })
  ) {
    return false
  }

  // from
  if (
    schema.from !== undefined &&
    !checkValueIsArrayIn({
      value: transfer.from,
      schema: schema.from,
      transaction,
      relatedAddresses,
    })
  ) {
    return false
  }

  // to
  if (
    schema.to !== undefined &&
    !checkValueIsArrayIn({
      value: transfer.to,
      schema: schema.to,
      transaction,
      relatedAddresses,
    })
  ) {
    return false
  }

  // amount
  if (schema.amount !== undefined) {
    if (typeof schema.amount === 'string') {
      if (transfer.value.toString() !== schema.amount) {
        return false
      }
    } else {
      switch (schema.amount.type) {
        case 'between': {
          if (
            !checkValueIsBetweenBigInt({
              value: transfer.value,
              schema: schema.amount,
            })
          ) {
            return false
          }
          break
        }
        case 'function':
        case 'from':
        case 'to':
        case 'value':
        case 'join':
        case 'slice':
        case 'chain-id':
        case 'tx-hash':
        case 'log':
        case 'erc20':
        case 'erc721':
        case 'erc1155':
        case 'math': {
          const value = getAllValue({
            schema: schema.amount,
            transaction,
            relatedAddresses,
          })
          if (transfer.value.toString() !== value) {
            return false
          }
          break
        }
        default: {
          throw new Error(
            `Unknown amount type: ${schema.amount satisfies never}`,
          )
        }
      }
    }
  }

  return true
}
